/**
 * 계측(Measure) 관련 GraphQL 쿼리/뮤테이션
 * 웹(@ict/web)과 동일한 리졸버 사용
 * Feature: #379
 *
 * NOTE: GraphQL 쿼리가 web과 중복됨 (DRY 위반)
 * @ict/shared에 @apollo/client 의존성이 없어 쿼리 공유 불가.
 * 향후 @ict/shared-graphql 패키지 분리 또는 모바일 전용 필드 최적화 시 해소 예정.
 * @see https://github.com/koreadcc/ict-web-nextjs/issues/385
 */

import { gql } from "@apollo/client";
import { type MeasureType, type JudgmentResultEnum } from "@ict/shared";

// ============================================
// Fragment: 24개 계측 타입 공통 필드
// equipment.ts의 PROJECT_MACHINE_GROUPS_QUERY에서도 재사용
// ============================================

/**
 * 24개 계측 타입 전체 필드 fragment
 * MeasuresType (shared/models/schemas/measures/Measures.schema.ts) 기준
 */
export const MEASURES_FIELDS_FRAGMENT = gql`
  fragment MeasuresFields on MeasuresType {
    # 전기 (7개)
    acVoltage {
      ratedVoltage
      measuredVoltage
      unit
      result
      discussion
      remark
      photo {
        checkUrl
        resultUrl
      }
      createdAt
    }
    dcVoltage {
      ratedVoltage
      measuredVoltage
      unit
      result
      discussion
      remark
      photo {
        checkUrl
        resultUrl
      }
      createdAt
    }
    groundResistance {
      label
      method
      measuredValue
      unit
      result
      discussion
      remark
      photo {
        checkUrl
        resultUrl
      }
      createdAt
    }
    terminationResistance {
      label
      standardValue
      measuredValue
      unit
      result
      discussion
      remark
      photo {
        checkUrl
        resultUrl
      }
      createdAt
    }
    chargingVoltageCurrent {
      chargingVoltage
      chargingCurrent
      unit
      result
      discussion
      remark
      photo {
        checkUrl
        resultUrl
      }
      createdAt
    }
    batteryVoltage {
      ratedVoltage
      batteryVoltage
      unit
      result
      discussion
      remark
      photo {
        checkUrl
        resultUrl
      }
      createdAt
    }
    clockLineVoltage {
      transmissionMethod
      measuredValue
      unit
      result
      discussion
      remark
      photo {
        checkUrl
        resultUrl
      }
      createdAt
    }
    # 통신환경 (5개)
    linkSpeed {
      designSpeed
      measuredSpeed
      unit
      result
      discussion
      remark
      photo {
        checkUrl
        resultUrl
      }
      createdAt
    }
    opticalLevel {
      seq
      deviceType
      measuredValue
      unit
      result
      discussion
      remark
      photo {
        checkUrl
        resultUrl
      }
      createdAt
    }
    txOutput {
      standardValue
      measuredValue
      unit
      result
      discussion
      remark
      photo {
        checkUrl
        resultUrl
      }
      createdAt
    }
    outputTerminalLevel {
      standardValue
      measuredValue
      unit
      result
      discussion
      remark
      photo {
        checkUrl
        resultUrl
      }
      createdAt
    }
    ioSignalLevel {
      inputStandard
      inputMeasured
      outputStandard
      outputMeasured
      unit
      result
      discussion
      remark
      photo {
        checkUrl
        resultUrl
      }
      createdAt
    }
    # 전파환경 (3개)
    rsrpTerrestrial {
      snr
      mer
      ber
      rssi
      location
      unit
      result
      discussion
      remark
      photo {
        checkUrl
        resultUrl
      }
      createdAt
    }
    rsrpFmRadio {
      snr
      mer
      ber
      rssi
      location
      unit
      result
      discussion
      remark
      photo {
        checkUrl
        resultUrl
      }
      createdAt
    }
    rsrpSatellite {
      snr
      mer
      ber
      rssi
      location
      unit
      result
      discussion
      remark
      photo {
        checkUrl
        resultUrl
      }
      createdAt
    }
    # 무선신호 (2개)
    emergencyBellSensitivity {
      standardValue
      measuredValue
      unit
      result
      discussion
      remark
      photo {
        checkUrl
        resultUrl
      }
      createdAt
    }
    remoteMeteringSensitivity {
      standardValue
      measuredValue
      unit
      result
      discussion
      remark
      photo {
        checkUrl
        resultUrl
      }
      createdAt
    }
    # 전계강도 (2개)
    coaxialFieldStrength {
      standardValue
      measuredValue
      unit
      result
      discussion
      remark
      photo {
        checkUrl
        resultUrl
      }
      createdAt
    }
    wirelessFieldStrength {
      standardValue
      measuredValue
      unit
      result
      discussion
      remark
      photo {
        checkUrl
        resultUrl
      }
      createdAt
    }
    # RF카드 (2개)
    rfMagneticField {
      label
      measuredValue
      unit
      result
      discussion
      remark
      photo {
        checkUrl
        resultUrl
      }
      createdAt
    }
    rfRecognitionDistance {
      label
      recognitionDistance
      unit
      result
      discussion
      remark
      photo {
        checkUrl
        resultUrl
      }
      createdAt
    }
    # 기타 (3개)
    temperatureHumidity {
      label
      temperature
      humidity
      unit
      result
      discussion
      remark
      photo {
        checkUrl
        resultUrl
      }
      createdAt
    }
    batterySpecificGravity {
      standardValue
      measuredValue
      unit
      result
      discussion
      remark
      photo {
        checkUrl
        resultUrl
      }
      createdAt
    }
    manholeToxicGas {
      oxygen
      oxygenResult
      hydrogenSulfide
      hydrogenSulfideResult
      carbonMonoxide
      carbonMonoxideResult
      unit
      result
      discussion
      remark
      photo {
        checkUrl
        resultUrl
      }
      createdAt
    }
  }
`;

// ============================================
// Query: 설비별 전체 계측 데이터 조회
// ============================================

/**
 * 설비별 전체 계측 데이터 조회 (24개 타입)
 * Measure.resolver.ts의 machineMeasures query 사용
 */
export const MACHINE_MEASURES_QUERY = gql`
  query MachineMeasures($projectId: ID!, $docKey: String!, $machineId: ID!) {
    machineMeasures(projectId: $projectId, docKey: $docKey, machineId: $machineId) {
      ...MeasuresFields
    }
  }
  ${MEASURES_FIELDS_FRAGMENT}
`;

// ============================================
// Mutation: 계측 항목 추가
// ============================================

/**
 * 계측 항목 추가
 * Measure.resolver.ts의 addMeasure mutation 사용
 */
export const ADD_MEASURE_MUTATION = gql`
  mutation AddMeasure($input: AddMeasureInput!) {
    addMeasure(input: $input) {
      success
      error
      errorCode
      newVersion
      measure
      clientMutationId
    }
  }
`;

// ============================================
// Mutation: 계측 항목 수정
// ============================================

/**
 * 계측 항목 수정
 * Measure.resolver.ts의 updateMeasure mutation 사용
 */
export const UPDATE_MEASURE_MUTATION = gql`
  mutation UpdateMeasure($input: UpdateMeasureInput!) {
    updateMeasure(input: $input) {
      success
      error
      errorCode
      newVersion
      measure
      clientMutationId
    }
  }
`;

// ============================================
// Mutation: 계측 항목 삭제
// ============================================

/**
 * 계측 항목 삭제
 * Measure.resolver.ts의 deleteMeasure mutation 사용
 */
export const DELETE_MEASURE_MUTATION = gql`
  mutation DeleteMeasure($input: DeleteMeasureInput!) {
    deleteMeasure(input: $input) {
      success
      error
      errorCode
      newVersion
      clientMutationId
    }
  }
`;

// ============================================
// TypeScript 타입 정의
// ============================================

/** 계측 사진 타입 */
export interface MeasurePhoto {
  checkUrl?: string;
  resultUrl?: string;
}

/** 계측 공통 필드 */
interface MeasureBase {
  unit?: string;
  result?: JudgmentResultEnum | null;
  discussion?: string;
  remark?: string;
  photo?: MeasurePhoto;
  createdAt?: string;
}

// 전기 (7개)
export interface AcVoltageMeasureData extends MeasureBase {
  ratedVoltage?: string;
  measuredVoltage?: string;
}

export interface DcVoltageMeasureData extends MeasureBase {
  ratedVoltage?: string;
  measuredVoltage?: string;
}

export interface GroundResistanceMeasureData extends MeasureBase {
  label?: string;
  method?: string;
  measuredValue?: string;
}

export interface TerminationResistanceMeasureData extends MeasureBase {
  label?: string;
  standardValue?: string;
  measuredValue?: string;
}

export interface ChargingVoltageMeasureData extends MeasureBase {
  chargingVoltage?: string;
  chargingCurrent?: string;
}

export interface BatteryVoltageMeasureData extends MeasureBase {
  ratedVoltage?: string;
  batteryVoltage?: string;
}

export interface ClockLineVoltageMeasureData extends MeasureBase {
  transmissionMethod?: string;
  measuredValue?: string;
}

// 통신환경 (5개)
export interface LinkSpeedMeasureData extends MeasureBase {
  designSpeed?: string;
  measuredSpeed?: string;
}

export interface OpticalLevelMeasureData extends MeasureBase {
  seq?: string;
  deviceType?: string;
  measuredValue?: string;
}

export interface TxOutputMeasureData extends MeasureBase {
  standardValue?: string;
  measuredValue?: string;
}

export interface OutputTerminalLevelMeasureData extends MeasureBase {
  standardValue?: string;
  measuredValue?: string;
}

export interface IoSignalLevelMeasureData extends MeasureBase {
  inputStandard?: string;
  inputMeasured?: string;
  outputStandard?: string;
  outputMeasured?: string;
}

// 전파환경 (3개)
export interface RsrpMeasureData extends MeasureBase {
  snr?: string;
  mer?: string;
  ber?: string;
  rssi?: string;
  location?: string;
}

// 무선신호 (2개) + 전계강도 (2개) + 축전지 비중 (공통 패턴)
export interface StandardMeasureData extends MeasureBase {
  standardValue?: string;
  measuredValue?: string;
}

// RF카드 - 자기장 세기
export interface RfMagneticFieldMeasureData extends MeasureBase {
  label?: string;
  measuredValue?: string;
}

// RF카드 - 인식거리
export interface RfRecognitionDistanceMeasureData extends MeasureBase {
  label?: string;
  recognitionDistance?: string;
}

// 기타 - 온습도
export interface TemperatureHumidityMeasureData extends MeasureBase {
  label?: string;
  temperature?: string;
  humidity?: string;
}

// 기타 - 맨홀 유해가스
export interface ManholeToxicGasMeasureData extends MeasureBase {
  oxygen?: string;
  oxygenResult?: string;
  hydrogenSulfide?: string;
  hydrogenSulfideResult?: string;
  carbonMonoxide?: string;
  carbonMonoxideResult?: string;
}

/** 24개 계측 타입 통합 (machineMeasures 쿼리 응답) */
export interface MachineMeasuresData {
  machineMeasures: {
    // 전기 (7개)
    acVoltage?: AcVoltageMeasureData[];
    dcVoltage?: DcVoltageMeasureData[];
    groundResistance?: GroundResistanceMeasureData[];
    terminationResistance?: TerminationResistanceMeasureData[];
    chargingVoltageCurrent?: ChargingVoltageMeasureData[];
    batteryVoltage?: BatteryVoltageMeasureData[];
    clockLineVoltage?: ClockLineVoltageMeasureData[];
    // 통신환경 (5개)
    linkSpeed?: LinkSpeedMeasureData[];
    opticalLevel?: OpticalLevelMeasureData[];
    txOutput?: TxOutputMeasureData[];
    outputTerminalLevel?: OutputTerminalLevelMeasureData[];
    ioSignalLevel?: IoSignalLevelMeasureData[];
    // 전파환경 (3개)
    rsrpTerrestrial?: RsrpMeasureData[];
    rsrpFmRadio?: RsrpMeasureData[];
    rsrpSatellite?: RsrpMeasureData[];
    // 무선신호 (2개)
    emergencyBellSensitivity?: StandardMeasureData[];
    remoteMeteringSensitivity?: StandardMeasureData[];
    // 전계강도 (2개)
    coaxialFieldStrength?: StandardMeasureData[];
    wirelessFieldStrength?: StandardMeasureData[];
    // RF카드 (2개)
    rfMagneticField?: RfMagneticFieldMeasureData[];
    rfRecognitionDistance?: RfRecognitionDistanceMeasureData[];
    // 기타 (3개)
    temperatureHumidity?: TemperatureHumidityMeasureData[];
    batterySpecificGravity?: StandardMeasureData[];
    manholeToxicGas?: ManholeToxicGasMeasureData[];
  } | null;
}

export interface MachineMeasuresVariables {
  projectId: string;
  docKey: string;
  machineId: string;
}

/** MeasureResponse (mutation 응답) */
export interface MeasureResponse {
  success: boolean;
  error?: string;
  errorCode?: string;
  newVersion?: number;
  measure?: Record<string, unknown>;
  clientMutationId?: string;
}

/** AddMeasureInput (mutation 입력) */
export interface AddMeasureInput {
  projectId: string;
  docKey: string;
  machineId: string;
  measureType: MeasureType;
  data: Record<string, unknown>;
  expectedVersion?: number;
  clientMutationId?: string;
}

/** UpdateMeasureInput (mutation 입력) */
export interface UpdateMeasureInput {
  projectId: string;
  docKey: string;
  machineId: string;
  measureType: MeasureType;
  measureIndex: number;
  data: Record<string, unknown>;
  expectedVersion?: number;
  clientMutationId?: string;
}

/** DeleteMeasureInput (mutation 입력) */
export interface DeleteMeasureInput {
  projectId: string;
  docKey: string;
  machineId: string;
  measureType: MeasureType;
  measureIndex: number;
  expectedVersion?: number;
  clientMutationId?: string;
}

/** AddMeasure mutation 응답 */
export interface AddMeasureData {
  addMeasure: MeasureResponse;
}

/** UpdateMeasure mutation 응답 */
export interface UpdateMeasureData {
  updateMeasure: MeasureResponse;
}

/** DeleteMeasure mutation 응답 (measure 필드 미요청) */
export interface DeleteMeasureData {
  deleteMeasure: Omit<MeasureResponse, "measure">;
}

export interface AddMeasureVariables {
  input: AddMeasureInput;
}

export interface UpdateMeasureVariables {
  input: UpdateMeasureInput;
}

export interface DeleteMeasureVariables {
  input: DeleteMeasureInput;
}
